import { loadStripe } from '@stripe/stripe-js';
import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import Request from '../../utils/request';
import Manipulator from '../../utils/dom/manipulator';
import ValidationUtils from '../../utils/validation';
import { addInvalidClasses, SELECTOR_ERROR_CLASS, validFormElementValue } from '../../utils/validateFormElement';
import BankCards from '../../components/BankCards';
import attachValidation from '../../utils/form-validation';
import { getDataFromForm } from '../../utils/misc';

const STEP_STATUSES = {
	PAYMENT_IN_PROGRESS: 'payment_in_progress',
	PAYMENT_SUCCESS: 'payment_success',
	PAYMENT_PROCESSING: 'payment_processing',
	PAYMENT_FAILED: 'payment_failed',
	CONFIRM_IN_PROGRESS: 'confirm_in_progress',
	CONFIRM_SUCCESS: 'confirm_success',
	CONFIRM_FAILED: 'confirm_failed',
	NOT_STARTED: 'not_started',
};

class CourseBookingForm {
	constructor() {
		this._wrapper = SelectorEngine.findOne('.js-course-booking-form');
		if (!this._wrapper) return;

		this._classID = new URLSearchParams(window.location.search).get('classId');
		this._searchSchoolsApiUrl = Manipulator.getDataAttribute(this._wrapper, 'search-schools-api-url');
		this._payCourseApiUrl = Manipulator.getDataAttribute(this._wrapper, 'pay-course-api-url');
		this._bookCourseApiUrl = Manipulator.getDataAttribute(this._wrapper, 'book-course-api-url');
		this._paymentsCaptureApiUrl = Manipulator.getDataAttribute(this._wrapper, 'payments-capture-api-url');
		this._isFreeCourse = Manipulator.getDataAttribute(this._wrapper, 'is-free-course');
		this._steps = SelectorEngine.find('[data-step]', this._wrapper);
		this._totalSteps = this._steps.length;
		this._currentStep = 1;
		this._paymentStatus = STEP_STATUSES.NOT_STARTED;
		this._backBtnContainer = SelectorEngine.findOne('.js-step-back-container', this._wrapper);
		this._backBtn = SelectorEngine.findOne('.js-step-back', this._wrapper);
		this._nextBtnContainer = SelectorEngine.findOne('.js-step-next-container', this._wrapper);
		this._nextBtn = SelectorEngine.findOne('.js-step-next', this._wrapper);
		this._payBtnContainer = SelectorEngine.findOne('.js-pay-container', this._wrapper);
		this._payBtn = SelectorEngine.findOne('.js-pay', this._wrapper);
		this._payBtnOriginText = this._payBtn?.innerText || 'Pay';
		this._confirmBtnContainer = SelectorEngine.findOne('.js-confirm-container', this._wrapper);
		this._confirmBtn = SelectorEngine.findOne('.js-confirm', this._wrapper);
		this._confirmBtnOriginText = this._confirmBtn?.innerText || 'Confirm booking';
		this._progress = SelectorEngine.findOne('.js-step-progress', this._wrapper);

		this._parentalConsentContainer = this._wrapper.querySelector('.js-parental-consent-container');
		this._ageOfAttendeeRadiosEl = this._wrapper.querySelectorAll('.js-toggle-parental-consent input[type="radio"]');
		this._parentFirstNameEl = this._wrapper.querySelector('#parentFirstName');
		this._parentLastNameEl = this._wrapper.querySelector('#parentLastName');
		this._parentalPermissionEl = this._wrapper.querySelector('#parentalPermission');

		this._accessRequirementsContainer = this._wrapper.querySelector('.js-access-requirements-container');
		this._accessRequirementsRadiosEl = this._wrapper.querySelectorAll('.js-toggle-access-requirements input[type="radio"]');

		this._additionalNeedsContainer = this._wrapper.querySelector('.js-additional-needs-container');
		this._additionalNeedsRadiosEl = this._wrapper.querySelectorAll('.js-toggle-additional-needs input[type="radio"]');

		this._schoolLookup = this._wrapper.querySelector('.js-school-lookup');
		this._schoolSearchInput = this._wrapper.querySelector('.js-school-search-input');
		this._schoolSearchInputEl = this._wrapper.querySelector('.js-school-search-input input');
		this._schoolSearchResults = this._wrapper.querySelector('.js-school-search-results');
		this._schoolSearchLoading = this._wrapper.querySelector('.js-school-search-loading');
		this._schoolSearchNoResults = this._wrapper.querySelector('.js-school-search-no-results');
		this._schoolIdInput = this._wrapper.querySelector('.js-school-id');
		this._schoolSearchTimeout = null;
		this._activeSchoolIndex = -1;

		this._playingStandardContainer = this._wrapper.querySelector('.js-playing-standard');

		this._deafCommunicationContainer = this._wrapper.querySelector('.js-deaf-communication');
		this._deafOtherContainer = this._wrapper.querySelector('.js-deaf-other-container');
		this._deafRadiosEl = this._wrapper.querySelectorAll('.js-toggle-deaf-other input[type="radio"]');

		this._visuallyImpairedContainer = this._wrapper.querySelector('.js-visually-impaired');
		this._wheelchairSizeContainer = this._wrapper.querySelector('.js-wheelchair-size');

		this._emergencyContactContainer = this._wrapper.querySelector('.js-emergency-contact');
		this._emergencyFirstNameEl = this._wrapper.querySelector('#emergencyFirstName');
		this._emergencyLastNameEl = this._wrapper.querySelector('#emergencyLastName');
		this._emergencyContactNumberEl = this._wrapper.querySelector('#emergencyContactNumber');

		this._photographyContainer = this._wrapper.querySelector('.js-photography-container');
		this._photographyRadiosEl = this._wrapper.querySelectorAll('.js-toggle-photography input[type="radio"]');
		this._photographyAgeRadiosEl = this._wrapper.querySelectorAll('.js-toggle-photography-age input[type="radio"]');
		this._photoOver18Radio = this._wrapper.querySelector('#photoOver18');

		this._termsAcceptedEl = this._wrapper.querySelector('#termsAccepted');

		this._paymentValidation = undefined;
		this._cardsContainer = this._wrapper.querySelector('.js-cards-container');
		this._chargedContainer = this._wrapper.querySelector('.js-charged-container');
		this._processingContainer = this._wrapper.querySelector('.js-processing-container');
		this._paidContainer = this._wrapper.querySelector('.js-paid-container');
		this._confirmationDetailsContainer = this._wrapper.querySelector('.js-confirmation-details-container');
		this._snackbarContainer = this._wrapper.querySelector('.js-snackbar-container');
		this._snackbarError = this._wrapper.querySelector('.js-snackbar-error');

		EventHandler.on(this._nextBtn, 'click', () => {
			this._goTo(this._currentStep + 1);
			this._updateStepControlsUI();
		});
		EventHandler.on(this._backBtn, 'click', () => {
			this._goTo(this._currentStep - 1, { skipValidation: true });
			this._updateStepControlsUI();
		});
		EventHandler.on(this._payBtn, 'click', () => this._initPayment());
		EventHandler.on(this._confirmBtn, 'click', () => this._bookCourse());
		EventHandler.on(this._wrapper.querySelector('.js-snackbar-close'), 'click', () => this._snackbarError.classList.add('u-hidden'));
		this._formRules = {
			attendeeAge: {input: this._ageOfAttendeeRadiosEl, errorMessage: 'Age of attendee is required'},
			parentFirstName: {input: this._parentFirstNameEl, errorMessage: 'Your first name is required'},
			parentLastName: {input: this._parentLastNameEl, errorMessage: 'Your last name is required'},
			parentalPermission: {input: this._parentalPermissionEl, errorMessage: 'Please grant permission'},
			photographyPermission: {input: this._photographyRadiosEl, errorMessage: 'Use Photography option is required'},
			photographyAgePermission: {input: this._photographyAgeRadiosEl, errorMessage: 'Please choose one of the photography options'},
			emergencyFirstName: {input: this._emergencyFirstNameEl, errorMessage: 'Emergency first name is required'},
			emergencyLastName: {input: this._emergencyLastNameEl, errorMessage: 'Emergency last name is required'},
			emergencyContactNumber: {
				input: this._emergencyContactNumberEl,
				errorMessage: 'Emergency contact number is required',
				patterns: [
					{ regex: /^[\d\s]+$/, errorMessage: 'Phone number must contain digits only' },
					{ regex: /^.{9,}$/, errorMessage: 'Phone number must be between 9 and 15 characters' },
				]
			},
			termsAccepted: {input: this._termsAcceptedEl, errorMessage: 'Please read through and accept terms and conditions'},
		};

		this._initStepValidation();
		this._initBankCards();
		this._initNewCardValidation();
		this._initSchoolSearch();
		this._initAgeOfAttendeeUI();
		this._initAccessRequirementsUI();
		this._initAdditionalNeedsUI();
		this._initDeafCommunicationUI();
		this._initPhotographyUI();
		this._updateStepControlsUI();
		this._updatePaymentUI();
		this._updateConfirmationDetailsUI();
	}

	_getFieldValue(input, isGroup) {
		if (isGroup) return [...input].some(r => r.checked) ? input[0].value : '';
		if (input.type === 'checkbox') return input.checked ? 'on' : '';
		return input.value?.trim();
	}

	_validateField = (rule) => {
		const { input, patterns, wrapperSelector, ...opts } = rule;
		const isGroup = input instanceof NodeList;
		const el = isGroup ? input[0] : input;
		if (!el || el.closest('[hidden]')) return true;

		const element = el.closest(wrapperSelector || ValidationUtils.getFieldWrapper(el.type));
		const value = this._getFieldValue(input, isGroup);

		if (!validFormElementValue(value, { element, ...opts })) return false;

		return !patterns?.some(({ regex, errorMessage }) => {
			if (regex.test(value)) return false;
			addInvalidClasses(element);
			const errorEl = element?.querySelector(SELECTOR_ERROR_CLASS);
			if (errorEl) errorEl.innerHTML = errorMessage || opts.errorMessage;
			return true;
		});
	};

	_initStepValidation() {
		Object.values(this._formRules).forEach((rule) => {
			if (rule.input instanceof NodeList) {
				rule.input.forEach(el => el.addEventListener('change', () => this._validateField(rule)));
			} else {
				const event = rule.input?.tagName === 'SELECT' || rule.input?.type === 'checkbox' ? 'change' : 'input';
				rule.input?.addEventListener(event, () => this._validateField(rule));
			}
		});
	}

	_validateStepForm() {
		return Object.values(this._formRules).map((rule) => this._validateField(rule)).every(Boolean);
	}

	_initNewCardValidation() {
		this._wrapper.addEventListener('bankcards.valid', () => {
			this._payment();
		});
	}

	_initExistingCardsValidation() {
		if (!this._existingCardsForm) return;
		const cardValidators = {
			validators: {
				notEmpty: {
					message: 'Please select one payment option below, or alternatively add a new payment type'
				}
			}
		};
		this._existingCardsForm.addEventListener('addCard', (event) => this.addFieldToValidation(event, cardValidators));
		this._paymentValidation = attachValidation(this._existingCardsForm, {
			CardTokenId: cardValidators
		});
		this._paymentValidation.on('core.form.valid', async () => this._payment());
	}

	_isNewCard() {
		return this._bankCards._newCardView && !this._bankCards._newCardView.classList.contains('u-hidden');
	}

	_isSaveCardChecked() {
		return !!this._saveCardEl?.checked;
	}

	_initPayment() {
		if (this._isNewCard()) {
			this._newCardForm?.requestSubmit();
			return;
		}
		this._existingCardsForm?.requestSubmit();
	}

	addFieldToValidation(event, validators) {
		this._paymentValidation
			.addField('CardTokenId', validators)
			.resetField('CardTokenId', true);
	}

	_goTo(step, { skipValidation = false } = {}) {
		if (step < 1 || step > this._totalSteps) return;
		if (!skipValidation && !this._validateStepForm()) {
			this._wrapper.querySelector('.invalid-field, .is-invalid')?.scrollIntoView({behavior: 'smooth', block: 'center'});
			return;
		}
		this._currentStep = step;
		// Rebuild BankCards while step 1 is shown so step 2 is always fresh (no stale values).
		if (step === 1) this._initBankCards();
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	_initBankCards() {
		// Free courses skip payment entirely, so don't mount Stripe or fetch saved cards.
		if (this._isFreeCourse) return;
		// First run snapshots the pristine markup; later runs restore it so the new
		// instance mounts Stripe onto empty nodes with no stale listeners or values.
		if (this._bankCardsHTML === undefined) {
			this._bankCardsHTML = this._cardsContainer.innerHTML;
		} else {
			this._cardsContainer.innerHTML = this._bankCardsHTML;
		}
		this._bankCards = new BankCards('.js-bank-cards', { externalSubmit: true });
		this._existingCardsForm = SelectorEngine.findOne('#booking-checkout');
		this._newCardForm = SelectorEngine.findOne('#lta-bank-cards--form');
		this._saveCardEl = this._wrapper.querySelector('#saveCard');
		this._initExistingCardsValidation();
	}

	_initSchoolSearch() {
		if (!this._schoolSearchInputEl) return;

		document.addEventListener('click', (e) => {
			if (!this._schoolSearchInput?.contains(e.target)) this._renderSchoolResults([]);
		});

		this._schoolSearchInputEl.addEventListener('keydown', (e) => {
			const options = this._schoolSearchResults.querySelectorAll('[role="option"]');
			if (!options.length) return;

			if (e.key === 'ArrowDown') {
				e.preventDefault();
				this._activeSchoolIndex = Math.min(this._activeSchoolIndex + 1, options.length - 1);
				this._setActiveSchoolOption(options);
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				this._activeSchoolIndex = Math.max(this._activeSchoolIndex - 1, 0);
				this._setActiveSchoolOption(options);
			} else if (e.key === 'Enter' && this._activeSchoolIndex >= 0) {
				e.preventDefault();
				options[this._activeSchoolIndex].click();
			} else if (e.key === 'Escape') {
				this._renderSchoolResults([]);
			}
		});

		this._schoolSearchInputEl.addEventListener('input', (e) => {
			clearTimeout(this._schoolSearchTimeout);
			const query = e.target.value.trim();
			if (this._schoolIdInput) this._schoolIdInput.value = '';
			if (query.length < 3) {
				this._renderSchoolResults([]);
				return;
			}
			this._schoolSearchTimeout = setTimeout(() => this._fetchSchools(query), 1000);
		});
	}

	_setActiveSchoolOption(options) {
		for (const opt of options) opt.classList.remove('is-active');
		const active = options[this._activeSchoolIndex];
		active.classList.add('is-active');
		active.scrollIntoView({ block: 'nearest' });
		this._schoolSearchInputEl.setAttribute('aria-activedescendant', active.id);
	}

	_renderSchoolResults(schools) {
		this._schoolSearchResults.innerHTML = '';
		const hasItems = schools.length > 0;
		this._schoolSearchResults.hidden = !hasItems;
		if (this._schoolSearchInputEl) {
			this._schoolSearchInputEl.setAttribute('aria-expanded', String(hasItems));
			if (!hasItems) this._schoolSearchInputEl.removeAttribute('aria-activedescendant');
		}
		if (!hasItems) return;

		this._activeSchoolIndex = -1;
		for (const [i, school] of schools.entries()) {
			const li = document.createElement('li');
			li.id = `school-option-${i}`;
			li.className = 'p-course-booking-form__school-result-item';
			li.setAttribute('role', 'option');
			li.innerHTML = `
				<span class="u-body1">${school.name}</span>
				<span class="u-body2 u-text-grey-ui-mid">${school.postcode}</span>
			`;
			li.addEventListener('click', () => {
				this._schoolSearchInputEl.value = school.name;
				if (this._schoolIdInput) this._schoolIdInput.value = school.id;
				this._renderSchoolResults([]);
			});
			this._schoolSearchResults.append(li);
		}
	}

	_initAgeOfAttendeeUI() {
		if (!this._parentalConsentContainer || !this._ageOfAttendeeRadiosEl.length) return;

		this._ageOfAttendeeRadiosEl.forEach(radio => {
			EventHandler.on(radio, 'change', () => {
				const isUnder18 = radio.value === 'Under 18';
				this._parentalConsentContainer.hidden = !isUnder18;
				if (this._photoOver18Radio) {
					this._photoOver18Radio.disabled = isUnder18;
					if (isUnder18) this._photoOver18Radio.checked = false;
				}
			});
		});
	}

	_initPhotographyUI() {
		if (!this._photographyContainer || !this._photographyRadiosEl.length) return;

		this._photographyRadiosEl.forEach(radio => {
			EventHandler.on(radio, 'change', () => {
				this._photographyContainer.hidden = radio.value !== 'Yes';
			});
		});
	}

	_initAdditionalNeedsUI() {
		if (!this._additionalNeedsContainer || !this._additionalNeedsRadiosEl.length) return;

		this._additionalNeedsRadiosEl.forEach(radio => {
			EventHandler.on(radio, 'change', () => {
				this._additionalNeedsContainer.hidden = radio.value !== 'Yes';
			});
		});
	}

	_initDeafCommunicationUI() {
		if (!this._deafOtherContainer || !this._deafRadiosEl.length) return;

		this._deafRadiosEl.forEach(radio => {
			EventHandler.on(radio, 'change', () => {
				this._deafOtherContainer.hidden = radio.value !== 'Other';
			});
		});
	}

	_initAccessRequirementsUI() {
		if (!this._accessRequirementsContainer || !this._accessRequirementsRadiosEl.length) return;

		this._accessRequirementsRadiosEl.forEach(radio => {
			EventHandler.on(radio, 'change', () => {
				this._accessRequirementsContainer.hidden = radio.value !== 'Yes';
			});
		});
	}

	_updateStepControlsUI() {
		this._steps.forEach(el => el.hidden = +el.dataset.step !== this._currentStep);
		if (this._backBtnContainer && this._nextBtnContainer && this._payBtn && this._confirmBtnContainer && this._confirmBtn) {
			this._payBtn.innerText = this._payBtnOriginText;
			this._confirmBtn.innerText = this._confirmBtnOriginText;
			this._payBtn.disabled = false;
			this._confirmBtn.disabled = false;
			if (this._currentStep === 1) {
				this._backBtnContainer.hidden = true;
				this._nextBtnContainer.hidden = false;
				this._payBtnContainer.hidden = true;
				this._confirmBtnContainer.hidden = true;
			}
			if (this._currentStep === 2) {
				this._backBtnContainer.hidden = false;
				if (this._paymentStatus === STEP_STATUSES.PAYMENT_IN_PROGRESS) {
					this._nextBtnContainer.hidden = true;
					this._confirmBtnContainer.hidden = true;
					this._payBtnContainer.hidden = false;
					this._payBtn.disabled = true;
					this._payBtn.innerText = 'Processing...';
				} else if (this._paymentStatus === STEP_STATUSES.PAYMENT_SUCCESS) {
					this._nextBtnContainer.hidden = false;
					this._confirmBtnContainer.hidden = true;
					this._payBtnContainer.hidden = true;
				} else if (this._paymentStatus === STEP_STATUSES.PAYMENT_PROCESSING) {
					this._nextBtnContainer.hidden = true;
					this._confirmBtnContainer.hidden = true;
					this._payBtnContainer.hidden = true;
				} else if (this._paymentStatus === STEP_STATUSES.PAYMENT_FAILED) {
					this._nextBtnContainer.hidden = true;
					this._confirmBtnContainer.hidden = true;
					this._payBtnContainer.hidden = false;
				} else if (this._paymentStatus === STEP_STATUSES.CONFIRM_IN_PROGRESS) {
					this._nextBtnContainer.hidden = true;
					this._payBtnContainer.hidden = true;
					this._confirmBtnContainer.hidden = false;
					this._confirmBtn.disabled = true;
					this._confirmBtn.innerText = 'Processing...';
				} else if (this._paymentStatus === STEP_STATUSES.CONFIRM_SUCCESS) {
					this._nextBtnContainer.hidden = false;
					this._confirmBtnContainer.hidden = true;
					this._payBtnContainer.hidden = true;
				} else if (this._paymentStatus === STEP_STATUSES.CONFIRM_FAILED) {
					this._nextBtnContainer.hidden = true;
					this._confirmBtnContainer.hidden = false;
					this._payBtnContainer.hidden = true;
				} else {
					this._nextBtnContainer.hidden = true;
					if (this._isFreeCourse) {
						this._confirmBtnContainer.hidden = false;
						this._payBtnContainer.hidden = true;
					} else {
						this._confirmBtnContainer.hidden = true;
						this._payBtnContainer.hidden = false;
					}
				}
			}
			if (this._currentStep === 3) {
				this._backBtnContainer.hidden = true;
				this._nextBtnContainer.hidden = true;
				this._payBtnContainer.hidden = true;
				this._confirmBtnContainer.hidden = true;
			}
		}
		if (this._progress) this._progress.innerHTML = `
			<div class="lta-steps__wrapper" aria-role="progress" aria-label="step ${this._currentStep} of ${this._totalSteps}">${
				Array.from({ length: this._totalSteps }, (_, i) =>
					`<div class="lta-steps__item steps__item-${i + 1}${i < this._currentStep ? ' u-bg-lta-blue' : ''}"></div>`
				).join('')
			}</div>`;
	}

	_updatePaymentUI() {
		const isSuccess = this._paymentStatus === STEP_STATUSES.PAYMENT_SUCCESS;
		const isProcessing = this._paymentStatus === STEP_STATUSES.PAYMENT_PROCESSING;
		this._cardsContainer.hidden = this._isFreeCourse || isSuccess || isProcessing;
		this._chargedContainer.hidden = this._isFreeCourse || isSuccess || isProcessing;
		this._processingContainer.hidden = this._isFreeCourse || !isProcessing;
		this._paidContainer.hidden = this._isFreeCourse || !isSuccess;
	}

	_updateConfirmationDetailsUI() {
		this._confirmationDetailsContainer.hidden = !this._isFreeCourse
	}

	_showError({ message = '' }) {
		if (!this._snackbarError) return;
		const popupTitle = SelectorEngine.findOne('.title', this._snackbarError);
		popupTitle.innerText = message;
		this._snackbarError.classList.remove('u-hidden');
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	_radioValue(name) {
		return this._wrapper.querySelector(`input[name="${name}"]:checked`)?.value || '';
	}

	_inputValue(selector) {
		return this._wrapper.querySelector(selector)?.value?.trim() || '';
	}

	_isChecked(selector) {
		return !!this._wrapper.querySelector(selector)?.checked;
	}

	_paymentPayload(cardDetails = {}) {
		const orderTotal = parseFloat(Manipulator.getDataAttribute(SelectorEngine.findOne('[data-order-total]', this._wrapper), 'order-total'));
		return {
			classId: this._classID || '',
			retainCard: cardDetails.retainCard || false,
			paymentId: cardDetails.cardTokenId,
			stripeCustomerRef: cardDetails.customerRef,
			orderTotal: Number.isFinite(orderTotal) && orderTotal >= 0 ? orderTotal : null,
			isReqAuthorizationSuccess: "",
			formDetails: {
				schoolId: this._inputValue('.js-school-id'),
				playingStandard: this._radioValue('playingStandard'),
				age18AndOver: this._radioValue('ageOfAttendee') === '18 and over',
				age18AndOverParentFirstName: this._inputValue('#parentFirstName'),
				age18AndOverParentLastName: this._inputValue('#parentLastName'),
				age18AndOverParentRelationship: this._inputValue('#parentRelationship'),
				age18AndOverParentalPermission: this._isChecked('#parentalPermission'),
				accessRequirements: this._radioValue('accessRequirements') === 'Yes',
				accessRequirementsDetails: this._inputValue('#accessDetails'),
				additionalNeeds: this._radioValue('additionalNeeds') === 'Yes',
				additionalNeedsDetails: this._inputValue('#additionalNeedsDetails'),
				wheelchairSize: this._radioValue('wheelchairSize'),
				visualImpairment: this._radioValue('visualImpairment'),
				communicationMethod: this._radioValue('communicationMethod'),
				communicationDetails: this._inputValue('#deafOtherDetails'),
				emergencyFirstName: this._inputValue('#emergencyFirstName'),
				emergencyLastName: this._inputValue('#emergencyLastName'),
				emergencyContactNumber: this._inputValue('#emergencyContactNumber'),
				photographyAllowed: this._radioValue('photography') === 'Yes',
				photographyRole: this._radioValue('allowPhoto'),
			},
		};
	}

	_bookPayload() {
		return {
			classId: this._classID || '',
			formDetails: {
				schoolId: this._inputValue('.js-school-id'),
				playingStandard: this._radioValue('playingStandard'),
				age18AndOver: this._radioValue('ageOfAttendee') === '18 and over',
				age18AndOverParentFirstName: this._inputValue('#parentFirstName'),
				age18AndOverParentLastName: this._inputValue('#parentLastName'),
				age18AndOverParentRelationship: this._inputValue('#parentRelationship'),
				age18AndOverParentalPermission: this._isChecked('#parentalPermission'),
				accessRequirements: this._radioValue('accessRequirements') === 'Yes',
				accessRequirementsDetails: this._inputValue('#accessDetails'),
				additionalNeeds: this._radioValue('additionalNeeds') === 'Yes',
				additionalNeedsDetails: this._inputValue('#additionalNeedsDetails'),
				wheelchairSize: this._radioValue('wheelchairSize'),
				visualImpairment: this._radioValue('visualImpairment'),
				communicationMethod: this._radioValue('communicationMethod'),
				communicationDetails: this._inputValue('#deafOtherDetails'),
				emergencyFirstName: this._inputValue('#emergencyFirstName'),
				emergencyLastName: this._inputValue('#emergencyLastName'),
				emergencyContactNumber: this._inputValue('#emergencyContactNumber'),
				photographyAllowed: this._radioValue('photography') === 'Yes',
				photographyRole: this._radioValue('allowPhoto'),
			},
		};
	}

	async _fetchSchools(query) {
		this._schoolSearchLoading.hidden = false;
		this._schoolSearchNoResults.hidden = true;
		this._schoolSearchResults.hidden = true;

		try {
			const response = await Request.get(this._searchSchoolsApiUrl, { q: query });
			const schools = response.responseContainerContents || [];
			this._renderSchoolResults(schools);
			if (!schools.length) this._schoolSearchNoResults.hidden = false;
		} catch {
			this._schoolSearchNoResults.hidden = false;
			this._renderSchoolResults([]);
		} finally {
			this._schoolSearchLoading.hidden = true;
		}
	}

	async _payment() {
		let cardDetails = {};
		this._snackbarError.classList.add('u-hidden');
		this._paymentStatus = STEP_STATUSES.PAYMENT_IN_PROGRESS;
		this._updateStepControlsUI();

		if (this._isNewCard()) {
			const {cardTokenId, customerRef} = await this._createPaymentMethodToken();
			if (cardTokenId && customerRef) {
				cardDetails = {cardTokenId, customerRef, retainCard: this._isSaveCardChecked()};
			} else {
				this._paymentStatus = STEP_STATUSES.PAYMENT_FAILED;
				this._updatePaymentUI();
				this._updateStepControlsUI();
				return;
			}
		} else {
			const dataObject = getDataFromForm(this._existingCardsForm);
			cardDetails = {cardTokenId: dataObject.CardTokenId, customerRef: dataObject.CustomerId, retainCard: true};
		}

		const paymentRequest = await Request.post(this._payCourseApiUrl, this._paymentPayload(cardDetails))
			.catch(() => {
				this._paymentStatus = STEP_STATUSES.PAYMENT_FAILED;
				this._updatePaymentUI();
				this._updateStepControlsUI();
				this._showError({ message: 'We were unable to confirm your card payment.' });
			});

		// Stash so the 3DS capture call can tell the server which attendee to promote/rollback.
		const attendeeId = paymentRequest?.AttendeeId;

		switch (paymentRequest.Status) {
			case 'SUCCEEDED':
				this._paymentStatus = STEP_STATUSES.PAYMENT_SUCCESS;
				this._goTo(3);
				this._updatePaymentUI();
				this._updateStepControlsUI();
				break;
			case 'PROCESSING':
				this._paymentStatus = STEP_STATUSES.PAYMENT_PROCESSING;
				this._updatePaymentUI();
				this._updateStepControlsUI();
				break;
			case 'REQ_AUTHORIZATION':
				// Connected-account course payments create the PaymentIntent as a direct charge on
				// the provider's Stripe account. Those connected accounts live under the S_KEY
				// (Stripe:PublishableKey) platform — the same platform createPaymentMethod uses —
				// so Stripe.js must be initialised with S_KEY and scoped to that account with
				// { stripeAccount }, or confirmCardPayment cannot resolve the intent and fails with
				// "No such payment_intent". Fall back to the shared _stripeLTA instance when the
				// backend did not return a connected account (LTA platform-account charge).
				const stripeForConfirm = paymentRequest.ConnectedAccountId
					? await loadStripe(window.S_KEY, { stripeAccount: paymentRequest.ConnectedAccountId })
					: this._bankCards._stripeLTA;
				const { paymentIntent, error } = await stripeForConfirm
					.confirmCardPayment(paymentRequest.ClientSecret || '')
					.catch(() => {
						this._showError({ message: 'We were unable to confirm your card payment.' });
					});
				if (error) {
					this._paymentStatus = STEP_STATUSES.PAYMENT_FAILED;
					this._showError({ message: error.message });
					this._updatePaymentUI();
					this._updateStepControlsUI();
					await Request.post(this._paymentsCaptureApiUrl, {
						PaymentIntentId: error.payment_intent.id,
						ClassId: this._classID,
						AttendeeId: attendeeId,
						ConnectedAccountId: paymentRequest.ConnectedAccountId
					});
				} else {
					await Request.post(this._paymentsCaptureApiUrl, {
						PaymentIntentId: paymentIntent.id,
						ClassId: this._classID,
						AttendeeId: attendeeId,
						ConnectedAccountId: paymentRequest.ConnectedAccountId
					})
						.then((response) => {
							if(response.Status) {
								this._paymentStatus = STEP_STATUSES.PAYMENT_SUCCESS;
								this._goTo(3);
								this._updatePaymentUI();
								this._updateStepControlsUI();
							} else {
								this._paymentStatus = STEP_STATUSES.PAYMENT_FAILED;
								this._updatePaymentUI();
								this._updateStepControlsUI();
								this._showError({ message: 'We were unable to confirm your card payment.' });
							}
						}).catch(() => {
							this._paymentStatus = STEP_STATUSES.PAYMENT_FAILED;
							this._updatePaymentUI();
							this._updateStepControlsUI();
							this._showError({ message: 'We were unable to confirm your card payment.' });
						});
				}
				break;
			default:
				this._paymentStatus = STEP_STATUSES.PAYMENT_FAILED;
				this._updatePaymentUI();
				this._updateStepControlsUI();
				this._showError({ message: paymentRequest.ErrorMessage });
		}
	}

	async _createPaymentMethodToken() {
		const [cardElement] = this._bankCards.state.stripeElements;
		let address = this._bankCards.addressSearch.state.useRegisteredAddress
			? this._bankCards.addressSearch.state.address
			: this._bankCards.state.registerAddress;

		const billingDetails = {
			name: this._bankCards._cardNameElement.value,
			address: {
				line1: address.street,
				city: address.city,
				country: 'GB',
				postal_code: address.postCode
			}
		};

		const stripeResponse = await this._bankCards._stripe.createPaymentMethod({
			type: 'card',
			card: cardElement,
			billing_details: billingDetails
		});

		if (stripeResponse.error) {
			this._showError({ message: stripeResponse.error.message });
			return {};
		}

		const pmId = stripeResponse.paymentMethod.id;

		// const savedCard = await Request.post('/api/payments/card', {
		// 	stripeCustomerRef: this._bankCards.state.customerRef,
		// 	cardToken: stripeResponse.paymentMethod.id
		// }).catch(() => null);
		//
		// if (!savedCard) {
		// this._showError({ message: 'We have had some troubles adding the card.' });
		// 	return;
		// }
		//
		// if (savedCard.Status === 'REQ_AUTHORIZATION') {
		// 	const { error } = await this._bankCards._stripe
		// 		.confirmCardSetup(savedCard.ClientSecret, {
		// 			payment_method: { card: cardElement, billing_details: billingDetails }
		// 		})
		// 		.catch(() => this._showError({ message: 'We were unable to add your card.' }));
		//
		// 	if (error) {
		// 		this._showError({ message: error.message })
		// 		return;
		// 	}
		// } else if (!savedCard.Success) {
		// 	this._showError({ message: savedCard.ErrorMessage || 'We were unable to save your card.' })
		// 	return;
		// }

		return { cardTokenId: pmId, customerRef: this._bankCards.state.customerRef };
	}

	async _bookCourse() {
		this._snackbarError.classList.add('u-hidden');
		this._paymentStatus = STEP_STATUSES.CONFIRM_IN_PROGRESS;
		this._updateStepControlsUI();
		try {
			const bookResponse = await Request.post(this._bookCourseApiUrl, this._bookPayload());
			if (bookResponse.Status === "SUCCEEDED") {
				this._paymentStatus = STEP_STATUSES.CONFIRM_SUCCESS;
				this._goTo(3);
			} else {
				this._showError({ message: bookResponse.ErrorMessage || 'Oops. Something has gone wrong!' });
				this._paymentStatus = STEP_STATUSES.CONFIRM_FAILED;
			}
		} catch (e) {
			this._showError({ message: 'Oops. Something has gone wrong!' });
			this._paymentStatus = STEP_STATUSES.CONFIRM_FAILED;
		} finally {
			this._updateStepControlsUI();
		}
	}
}

const globalCourseBookingForm = new CourseBookingForm();
export default globalCourseBookingForm;
